/**
 * Расширяемый линтер PlantUML (диаграммы последовательности)
 * Диагностика: { ruleId, message, line, severity, suggestion?, from?, to?, text? }
 */

export const Severity = { INFO: 'info', WARNING: 'warning', ERROR: 'error' };

// Вспомогательные регексы
const partRe = /^\s*participant\s+(\S+)/i;
const dbRe = /^\s*database\s+(\S+)/i;
const actRe = /^\s*activate\s+(\S+)/i;
const deactRe = /^\s*deactivate\s+(\S+)/i;
const msgRe = /^(\S+)\s*(-{1,2}>)\s*(\S+)\s*:(.+)$/; // -> или -->

// Универсальный парсер объявлений participant/database/actor с поддержкой alias через "as"
// groups: [type, name, alias]
const declWithAliasRe = /^\s*(participant|database|actor)\s+(\S+)(?:\s+as\s+("[^"]+"|'[^']+'|\S+))?/i;

function snippet(s, max = 40) {
  const t = (s || '').trim();
  if (t.length <= max) return t;
  return t.slice(0, max - 1) + '…';
}

// missing-reply
const missingReplyRule = {
  id: 'missing-reply',
  description: 'Отсутствует ответное сообщение B --> A на запрос A -> B',
  severity: Severity.WARNING,
  check(ctx) {
    /** @type {Array<{from:string,to:string,arrow:string,text:string,line:number}>} */
    const messages = [];
    const messageSet = new Set(); // from|to

    ctx.lines.forEach((raw, idx) => {
      const line = (raw || '').trim();
      if (!line || line.startsWith("'") || line.startsWith('//')) return;
      const m = msgRe.exec(line);
      if (m) {
        const from = m[1], arrow = m[2], to = m[3], text = m[4].trim();
        if (from === to) return;
        messages.push({ from, to, arrow, text, line: idx + 1 });
        messageSet.add(`${from}|${to}`);
      }
    });

    const diagnostics = [];
    for (const msg of messages) {
      if (msg.arrow === '->') {
        if (!messageSet.has(`${msg.to}|${msg.from}`)) {
          const s = snippet(msg.text);
          diagnostics.push({
            ruleId: this.id,
            message: `Нет ответного сообщения ${msg.to} --> ${msg.from} для запроса ${msg.from} -> ${msg.to}`,
            line: msg.line,
            severity: this.severity,
            suggestion: `${msg.to} --> ${msg.from} : ${s ? 'Ответ на: ' + s : 'response'}`,
            from: msg.from, to: msg.to, text: msg.text
          });
        }
      }
    }
    return diagnostics;
  }
};

// duplicate-messages: одинаковые From,To,Text
const duplicateMessagesRule = {
  id: 'duplicate-messages',
  description: 'Дублирующееся сообщение',
  severity: Severity.WARNING,
  check(ctx) {
    const seen = new Map(); // key from|to|text -> first line
    const diags = [];
    ctx.lines.forEach((raw, idx) => {
      const lineNo = idx + 1;
      const line = (raw || '').trim();
      if (!line || line.startsWith("'") || line.startsWith('//')) return;
      const m = msgRe.exec(line);
      if (!m) return;
      const from = m[1], to = m[3], text = m[4].trim();
      const key = `${from}|${to}|${text}`;
      if (seen.has(key)) {
        const first = seen.get(key);
        diags.push({
          ruleId: this.id,
          message: `Дубликат сообщения ${from} -> ${to}: "${snippet(text)}" (первое на строке ${first})`,
          line: lineNo,
          severity: this.severity,
          from, to, text
        });
      } else {
        seen.set(key, lineNo);
      }
    });
    return diags;
  }
};

// unknown-actor: использование неописанного участника
const unknownActorRule = {
  id: 'unknown-actor',
  description: 'Ссылка на неописанного участника',
  severity: Severity.WARNING,
  check(ctx) {
    const declared = new Set();
    const referenced = new Map(); // name -> first line

    ctx.lines.forEach((raw, idx) => {
      const line = (raw || '').trim();
      // Include actor declarations in declared set (item 4 fix)
      const m1 = partRe.exec(line) || dbRe.exec(line) || /^\s*actor\s+(\S+)/i.exec(line);
      if (m1) { declared.add(m1[1]); return; }

      let m;
      if ((m = msgRe.exec(line))) {
        const from = m[1], to = m[3];
        if (!referenced.has(from)) referenced.set(from, idx + 1);
        if (!referenced.has(to)) referenced.set(to, idx + 1);
      } else if ((m = actRe.exec(line)) || (m = deactRe.exec(line))) {
        const who = m[1];
        if (!referenced.has(who)) referenced.set(who, idx + 1);
      }
    });

    const diags = [];
    for (const [name, line] of referenced.entries()) {
      if (!declared.has(name)) {
        diags.push({
          ruleId: this.id,
          message: `Участник ${name} используется, но не объявлен (participant/database)`,
          line,
          severity: this.severity,
          suggestion: `participant ${name}`
        });
      }
    }
    return diags;
  }
};

// unmatched-deactivate: deactivate без предшествующего activate
const unmatchedDeactivateRule = {
  id: 'unmatched-deactivate',
  description: 'deactivate без предшествующего activate',
  severity: Severity.WARNING,
  check(ctx) {
    const stack = new Map(); // actor -> count
    const diags = [];
    ctx.lines.forEach((raw, idx) => {
      const line = (raw || '').trim();
      let m;
      if ((m = actRe.exec(line))) {
        const who = m[1];
        stack.set(who, (stack.get(who) || 0) + 1);
      } else if ((m = deactRe.exec(line))) {
        const who = m[1];
        if (!stack.get(who)) {
          diags.push({
            ruleId: this.id,
            message: `deactivate ${who} без предыдущего activate`,
            line: idx + 1,
            severity: this.severity,
            suggestion: `activate ${who}`
          });
        } else {
          stack.set(who, stack.get(who) - 1);
        }
      }
    });
    return diags;
  }
};

// dangling-participant: объявлен, но не участвует во взаимодействиях
const danglingParticipantRule = {
  id: 'dangling-participant',
  description: 'Участник объявлен, но не участвует во взаимодействиях',
  severity: Severity.INFO,
  check(ctx) {
    const declared = new Set();
    const used = new Set();

    ctx.lines.forEach((raw) => {
      const line = (raw || '').trim();
      let m;
      if ((m = partRe.exec(line)) || (m = dbRe.exec(line)) || (m = /^\s*actor\s+(\S+)/i.exec(line))) {
        declared.add(m[1]);
      } else if ((m = msgRe.exec(line))) {
        used.add(m[1]); used.add(m[3]);
      } else if ((m = actRe.exec(line)) || (m = deactRe.exec(line))) {
        used.add(m[1]);
      }
    });

    const diags = [];
    declared.forEach(name => {
      if (!used.has(name)) {
        diags.push({
          ruleId: this.id,
          message: `Участник ${name} не участвует ни в одном взаимодействии`,
        line: 1,
          severity: this.severity
        });
      }
    });
    return diags;
  }
};

// registry-unknown-participant: объявленный участник отсутствует в реестре ИС
const registryUnknownParticipantRule = {
  id: 'registry-unknown-participant',
  description: 'Объявленный участник отсутствует в реестре ИС',
  severity: Severity.ERROR,
  check(ctx) {
    const allowed = ctx.allowed || new Set();
    if (!allowed.size) return [];

    const diags = [];
    ctx.lines.forEach((raw, idx) => {
      const line = (raw || '').trim();
      const m = declWithAliasRe.exec(line);
      if (!m) return;
      // actor declarations are external users/systems — not required to be in the registry
      if (m[1].toLowerCase() === 'actor') return;
      const name = (m[2] || '').trim();
      const ok = allowed.has(name.toLowerCase());
      if (!ok) {
        diags.push({
          ruleId: this.id,
          message: `Участник ${name} не найден в реестре ИС`,
          line: idx + 1,
          severity: this.severity
        });
      }
    });
    return diags;
  }
};

// registry-undeclared-participant: неявный участник в сообщениях, не найденный в реестре
const registryUndeclaredParticipantRule = {
  id: 'registry-undeclared-participant',
  description: 'Участник используется в сообщениях, но не объявлен и отсутствует в реестре ИС',
  severity: Severity.ERROR,
  check(ctx) {
    const allowed = ctx.allowed || new Set();
    if (!allowed.size) return [];

    const declared = new Set();
    const used = new Map(); // name -> first line

    ctx.lines.forEach((raw, idx) => {
      const line = (raw || '').trim();

      // Объявленные участники
      const decl = declWithAliasRe.exec(line);
      if (decl) {
        declared.add(decl[2].trim());
        return;
      }

      // Используемые в сообщениях
      let m;
      if ((m = msgRe.exec(line))) {
        const from = m[1], to = m[3];
        if (!used.has(from)) used.set(from, idx + 1);
        if (!used.has(to)) used.set(to, idx + 1);
      } else if ((m = actRe.exec(line)) || (m = deactRe.exec(line))) {
        const who = m[1];
        if (!used.has(who)) used.set(who, idx + 1);
      }
    });

    const diags = [];
    for (const [name, line] of used.entries()) {
      if (!declared.has(name) && !allowed.has(name.toLowerCase())) {
        diags.push({
          ruleId: this.id,
          message: `Участник ${name} используется, но не объявлен и отсутствует в реестре ИС`,
          line,
          severity: this.severity,
          undeclaredActor: name
        });
      }
    }
    return diags;
  }
};

// Реестр правил
const rules = [
  missingReplyRule,
  duplicateMessagesRule,
  unknownActorRule,
  unmatchedDeactivateRule,
  danglingParticipantRule,
  registryUnknownParticipantRule,
  registryUndeclaredParticipantRule
];

export function lintPlantUml(text, options = {}) {
  if (!text || !/(@startuml|participant|database|--?>|<--?|->|<-)/i.test(text)) return [];
  const lines = text.split(/\r?\n/);
  const allowedList = Array.isArray(options.allowedParticipants) ? options.allowedParticipants : [];
  const allowed = new Set(allowedList.map((s) => String(s || '').trim().toLowerCase()).filter(Boolean));
  const ctx = { lines, allowed };
  const out = [];
  for (const rule of rules) {
    try {
      const res = rule.check(ctx) || [];
      for (const d of res) out.push(d);
    } catch (e) {
      console.warn('PlantUML lint rule failed', rule.id, e);
    }
  }
  return out.sort((a, b) => a.line - b.line);
}